import { useState } from "react";
import { Paperclip, ChevronDown } from "lucide-react";

/* A captured message is the conversation, quoted history and all. The new text
   is what anyone actually wants to read, so the chain underneath is collapsed
   rather than thrown away — it is still there when the detail matters. */

const QUOTE_MARKERS = [
  /<div[^>]*id=["']?appendonsend["']?/i,
  /<div[^>]*id=["']?divRplyFwdMsg["']?/i,
  /<blockquote/i,
  /-{3,}\s*original message\s*-{3,}/i,
  /_{20,}/,
  // Apple Mail and most phones: "On 8 Oct 2026, at 9:58 AM, Kevin wrote:"
  /On\s[^<>]{5,200}?wrote:/i,
  // Outlook's plain header block, with or without the rule above it.
  /<hr[^>]*>(?=[\s\S]{0,400}?from:)/i,
  /(?:<[^>]*>\s*)*From:\s*[^<>]{3,160}?(?:<[^>]*>\s*)*Sent:/i,
];

const textOf = (html) => String(html || "").replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ")
  .replace(/\s+/g, " ").trim();

export const splitQuoted = (html) => {
  const source = String(html || "");
  let at = -1;
  for (const rx of QUOTE_MARKERS) {
    const m = source.match(rx);
    if (m && m.index != null && (at === -1 || m.index < at)) at = m.index;
  }
  if (at < 0) return { fresh: source, quoted: "" };
  const fresh = source.slice(0, at);
  // "Unsubscribe" is a whole reply, and a short one is often the whole point —
  // so any readable text above the chain counts. Nothing readable, no split.
  if (!textOf(fresh)) return { fresh: source, quoted: "" };
  return { fresh, quoted: source.slice(at) };
};

const prettySize = (n) => {
  const bytes = Number(n) || 0;
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
};

export const Attachments = ({ items, testId }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={testId}>
      {items.map((a, i) => (
        <span key={`${a.name}-${i}`}
          className="inline-flex items-center gap-1.5 rounded-[6px] px-2 py-1 text-[11.5px]"
          style={{ background: "#F4F2ED", border: "1px solid #E6E2D9", color: "#59554D" }}
          data-testid={`${testId}-item-${i}`}>
          <Paperclip className="w-3 h-3 shrink-0" style={{ color: "#8A857A" }} />
          <span className="truncate max-w-[220px]" title={a.name}>{a.name}</span>
          <span className="mono text-[10.5px]" style={{ color: "#8A857A" }}>{prettySize(a.size)}</span>
        </span>
      ))}
    </div>
  );
};

export const EmailBody = ({ html, text, className = "pv-email-body", testId }) => {
  const [showQuoted, setShowQuoted] = useState(false);
  if (!html) {
    return text ? <p className="text-[13px] leading-[1.6] whitespace-pre-wrap"
      style={{ color: "#3A3630" }} data-testid={testId}>{text}</p> : null;
  }
  const { fresh, quoted } = splitQuoted(html);
  return (
    <div data-testid={testId}>
      {/* Third-party email HTML carries fixed-width signature tables that will
          not shrink, so the message scrolls on its own rather than pushing the
          page sideways on a phone. */}
      <div className={className} style={{ overflowX: "auto" }}
        dangerouslySetInnerHTML={{ __html: fresh }} />
      {quoted && (
        <>
          <button onClick={() => setShowQuoted((v) => !v)}
            className="inline-flex items-center gap-1 text-[11.5px] font-semibold mt-1.5 hover:underline"
            style={{ color: "#8A6D00" }} data-testid={testId ? `${testId}-quoted-toggle` : undefined}>
            {showQuoted ? "Hide the earlier messages" : "Show the earlier messages"}
            <ChevronDown className={`w-3 h-3 transition-transform ${showQuoted ? "rotate-180" : ""}`} />
          </button>
          {showQuoted && (
            <div className={`${className} mt-2 pl-3`}
              style={{ borderLeft: "2px solid #E6E2D9", opacity: 0.8, overflowX: "auto" }}
              dangerouslySetInnerHTML={{ __html: quoted }}
              data-testid={testId ? `${testId}-quoted` : undefined} />
          )}
        </>
      )}
    </div>
  );
};

/* Which way a captured message went, and that Outlook is where it came from —
   dressing a captured email up as a hand-logged touch would be a lie. */
export const DirectionBadge = ({ direction, captured, testId }) => {
  if (!direction && !captured) return null;
  const outbound = direction === "outbound";
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5 mb-0.5" data-testid={testId}>
      {direction && (
        <span className="mono text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wide"
          style={outbound
            ? { background: "#EEF4FF", color: "#2E5AAC", border: "1px solid #D5E1F6" }
            : { background: "#EAF3EC", color: "#0E6B3F", border: "1px solid #BFE0C9" }}>
          {outbound ? "From us" : "From them"}
        </span>
      )}
      {captured && (
        <span className="mono text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wide"
          style={{ background: "#F4F2ED", color: "#6B6660", border: "1px solid #E6E2D9" }}>
          Captured from Outlook
        </span>
      )}
    </span>
  );
};
